import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import TemplateCard from './TemplateCard';
import { fetchTemplateDocuments } from '../services/s3Service';

const TemplateLibraryGrid = ({
  selectedTemplates = [],
  onTemplateToggle,
  onSelectionChange,
  onTemplatesLoaded
}) => {
  // Debug marker to verify component version
  console.log('%cTLG-20250627-marker', 'color:#e91e63');
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  /* ------------------------------------------------------------------ */
  /*  Load templates once on mount                                       */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { success, templates: t } = await fetchTemplateDocuments();
        if (success && t) {
          setTemplates(t);
          onTemplatesLoaded?.(t);
        } else {
          setError('Failed to load templates');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load templates');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*  Keep parent in sync with current selections  */
  useEffect(() => {
    onSelectionChange?.(selectedTemplates);
  }, [selectedTemplates, onSelectionChange]);

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */
  const categories = ['all', ...new Set(templates.map(t => t.category).filter(Boolean))];

  const filteredTemplates = templates.filter(t => {
    const matchesSearch =
      !searchTerm ||
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const toggleTemplate = template => onTemplateToggle(template);

  const isSelected = template => selectedTemplates.some(t => t.id === template.id);

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* ─────────────────── Search + Category chips ─────────────────── */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search templates…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {categories.map(cat => (
            <Chip
              key={cat}
              label={cat === 'all' ? 'All Templates' : cat}
              onClick={() => setSelectedCategory(cat)}
              color={selectedCategory === cat ? 'primary' : 'default'}
              variant={selectedCategory === cat ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      </Box>

      {/* ─────────────────── Selected-docs summary chips ───────────────── */}
      {selectedTemplates.length > 0 && (
        <>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: 'primary.contrastText' }}>
              {selectedTemplates.length} document{selectedTemplates.length > 1 ? 's' : ''} selected
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
              {selectedTemplates
                .filter(t => t.type === 'document')
                .map(t => (
                  <Chip
                    key={t.id}
                    label={t.name}
                    size="small"
                    onDelete={() => toggleTemplate(t)}
                    sx={{
                      maxWidth: 200,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      bgcolor: 'white',
                      color: 'primary.main',
                      '& .MuiChip-deleteIcon': { color: 'primary.main' }
                    }}
                  />
                ))}
            </Stack>
          </Box>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {/* ─────────────────── Flex-wrap layout (2-col on ≥600px) ───── */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,                               // keeps the 24px spacing we had (spacing={3})
          '& > *': {
            flex: '1 1 100%',                   // full width by default (mobile)
            '@media (min-width: 600px)': {
              flex: '1 1 calc(50% - 12px)'     // two-up layout on ≥600px
            }
          }
        }}
      >
        {filteredTemplates.map(t => (
          <TemplateCard
            key={t.id || t.key}
            template={t}
            isSelected={isSelected(t)}
            onToggle={toggleTemplate}
          />
        ))}
      </Box>

      {filteredTemplates.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No templates found matching your criteria.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TemplateLibraryGrid;